import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import type { Asset, Dividend } from '../types';
import { ASSET_TYPE_LABELS } from '../types';

const BRAND = 'InvestFlow';
const TODAY = new Date().toLocaleDateString('pt-BR');

function formatBRL(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatPct(v: number) {
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

// ── PDF helpers ──────────────────────────────────────────────────────────────

function createPDF(title: string): jsPDF {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // Header
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, 297, 25, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(165, 180, 252);
  doc.text(BRAND, 14, 10);
  doc.setFontSize(10);
  doc.setTextColor(200, 200, 220);
  doc.text(title, 14, 17);
  doc.setFontSize(8);
  doc.setTextColor(120, 130, 150);
  doc.text(`Gerado em ${TODAY}`, 280, 17, { align: 'right' });

  return doc;
}

// ── Carteira — PDF ───────────────────────────────────────────────────────────

export function exportPortfolioToPDF(assets: Asset[]) {
  const doc = createPDF('Relatório de Carteira');

  const rows = assets.map((a) => {
    const invested = a.quantity * a.averagePrice;
    const current = a.quantity * (a.currentPrice ?? a.averagePrice);
    const gl = current - invested;
    const glPct = invested > 0 ? (gl / invested) * 100 : 0;
    return [
      a.code,
      a.name,
      ASSET_TYPE_LABELS[a.type] ?? a.type,
      a.quantity.toString(),
      formatBRL(a.averagePrice),
      formatBRL(a.currentPrice ?? a.averagePrice),
      formatBRL(invested),
      formatBRL(current),
      formatPct(glPct),
    ];
  });

  const totalInvested = assets.reduce((s, a) => s + a.quantity * a.averagePrice, 0);
  const totalCurrent = assets.reduce((s, a) => s + a.quantity * (a.currentPrice ?? a.averagePrice), 0);
  const totalGL = totalCurrent - totalInvested;
  const totalGLPct = totalInvested > 0 ? (totalGL / totalInvested) * 100 : 0;

  autoTable(doc, {
    startY: 30,
    head: [['Código', 'Nome', 'Tipo', 'Qtd', 'PM', 'Preço Atual', 'Investido', 'Atual', 'Ganho/Perda']],
    body: rows,
    foot: [['', '', '', '', '', 'Total', formatBRL(totalInvested), formatBRL(totalCurrent), formatPct(totalGLPct)]],
    headStyles: { fillColor: [30, 41, 59], textColor: [165, 180, 252], fontStyle: 'bold', fontSize: 8 },
    footStyles: { fillColor: [15, 23, 42], textColor: [200, 200, 220], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: [50, 50, 80] },
    alternateRowStyles: { fillColor: [245, 247, 255] },
    columnStyles: {
      0: { fontStyle: 'bold' },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' },
    },
  });

  doc.save(`carteira_${TODAY.replace(/\//g, '-')}.pdf`);
}

// ── Carteira — Excel ─────────────────────────────────────────────────────────

export function exportPortfolioToExcel(assets: Asset[]) {
  const rows = assets.map((a) => {
    const invested = a.quantity * a.averagePrice;
    const current = a.quantity * (a.currentPrice ?? a.averagePrice);
    const gl = current - invested;
    const glPct = invested > 0 ? (gl / invested) * 100 : 0;
    return {
      Código: a.code,
      Nome: a.name,
      Tipo: ASSET_TYPE_LABELS[a.type] ?? a.type,
      Setor: a.sector ?? '',
      Quantidade: a.quantity,
      'Preço Médio (R$)': a.averagePrice,
      'Preço Atual (R$)': a.currentPrice ?? a.averagePrice,
      'Total Investido (R$)': +invested.toFixed(2),
      'Valor Atual (R$)': +current.toFixed(2),
      'Ganho/Perda (R$)': +gl.toFixed(2),
      'Ganho/Perda (%)': +glPct.toFixed(2),
      Corretora: a.broker ?? '',
      'Primeira Compra': a.firstPurchaseDate,
    };
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [8, 24, 16, 14, 10, 16, 16, 18, 16, 16, 14, 14, 14].map((w) => ({ wch: w }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Carteira');
  XLSX.writeFile(wb, `carteira_${TODAY.replace(/\//g, '-')}.xlsx`);
}

// ── Proventos — PDF ──────────────────────────────────────────────────────────

export function exportDividendsToPDF(dividends: Dividend[]) {
  const doc = createPDF('Relatório de Proventos');

  const rows = dividends.map((d) => [
    d.assets?.code ?? '-',
    d.assets?.name ?? '-',
    d.type,
    new Date(d.paymentDate).toLocaleDateString('pt-BR'),
    formatBRL(d.valuePerShare),
    formatBRL(d.totalValue),
    d.isConfirmed ? 'Confirmado' : 'Previsto',
  ]);

  const total = dividends.reduce((s, d) => s + d.totalValue, 0);

  autoTable(doc, {
    startY: 30,
    head: [['Código', 'Nome', 'Tipo', 'Pagamento', 'Valor/Cota', 'Total', 'Status']],
    body: rows,
    foot: [['', '', '', '', 'Total', formatBRL(total), '']],
    headStyles: { fillColor: [30, 41, 59], textColor: [165, 180, 252], fontStyle: 'bold', fontSize: 8 },
    footStyles: { fillColor: [15, 23, 42], textColor: [200, 200, 220], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: [50, 50, 80] },
    alternateRowStyles: { fillColor: [245, 247, 255] },
    columnStyles: { 5: { halign: 'right' } },
  });

  doc.save(`proventos_${TODAY.replace(/\//g, '-')}.pdf`);
}

// ── Proventos — Excel ────────────────────────────────────────────────────────

export function exportDividendsToExcel(dividends: Dividend[]) {
  const rows = dividends.map((d) => ({
    Código: d.assets?.code ?? '-',
    Nome: d.assets?.name ?? '-',
    Tipo: d.type,
    'Data Pagamento': new Date(d.paymentDate).toLocaleDateString('pt-BR'),
    'Data Ex': d.exDate ? new Date(d.exDate).toLocaleDateString('pt-BR') : '',
    'Valor/Cota (R$)': d.valuePerShare,
    'Total (R$)': d.totalValue,
    Status: d.isConfirmed ? 'Confirmado' : 'Previsto',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [8, 24, 14, 14, 12, 14, 12, 12].map((w) => ({ wch: w }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Proventos');
  XLSX.writeFile(wb, `proventos_${TODAY.replace(/\//g, '-')}.xlsx`);
}
